import type { ReactElement } from 'react';
import { createElement } from 'react';
import { Icon } from '@material-ui/core';

import type { ISak } from './ISak';
import { SakKilde } from './SakKilde';
import type { Sakskategori } from './Sakskategori';
import type { Saksmelding } from './Saksmelding';
import { SakStatus } from './SakStatus';
import type { ISakStatus } from './SakStatus';

const statusIcons = {
    [SakStatus.Active.key]: 'description',
    [SakStatus.Closed.key]: 'timer_off',
    [SakStatus.Postponed.key]: '',
    [SakStatus.Merged.key]: '',
    [SakStatus.Deleted.key]: 'delete',
};

export class Sak implements ISak {
    public get id() {
        return this._id;
    }
    public get saksnummer() {
        return this._saksnummer;
    }
    public get created() {
        return this._created;
    }

    public get eksternSakId() {
        return this._eksternSakId;
    }

    public get emne() {
        return this._emne;
    }

    public get kundeNummer() {
        return this._kundeNummer;
    }

    public get opprettetAv() {
        return this._opprettetAv;
    }

    public get lukketDato() {
        return this._lukketDato;
    }

    public get sakskategoriId() {
        return this._sakskategoriId;
    }

    public get status() {
        return this._status;
    }

    public get sakskategori() {
        return this._sakskategori;
    }

    public get sakskategoriNavn() {
        return this._sakskategoriNavn;
    }

    public get saksmeldingList() {
        return this._saksmeldingList;
    }

    public get ekstraSakskategoriList() {
        return this._extraSakskategoriList;
    }

    public get isArchived() {
        return this._isArchived;
    }

    public get kilde() {
        return this._kilde;
    }

    public get eksternUrl() {
        return this._eksternUrl;
    }

    /**
     * Dalux-saker åpnes i Dalux. Uten lenke faller de tilbake til saksvisningen i Kundeportal.
     */
    public get isDalux() {
        return this._kilde === SakKilde.Dalux && !!this._eksternUrl;
    }

    public static parseObject(obj: ISak): Sak {
        try {
            return new Sak(obj);
        } catch (e) {
            throw new Error('Not able to parse object');
        }
    }

    public static empty() {
        return Sak.parseObject({
            emne: '',
            status: '',
            saksmeldingList: [],
            ekstraSakskategoriList: [],
            isArchived: false,
        });
    }

    public static icon(status: keyof ISakStatus): ReactElement {
        return createElement(Icon, { children: statusIcons[status] });
    }

    private readonly _emne: ISak['emne'];
    private readonly _status: ISak['status'];

    private readonly _kundeNummer?: ISak['kundeNummer'];
    private readonly _created?: ISak['created'];
    private readonly _eksternSakId?: ISak['eksternSakId'];
    private readonly _id?: ISak['id'];
    private readonly _saksnummer?: ISak['saksnummer'];
    private readonly _isArchived: ISak['isArchived'];
    private readonly _opprettetAv?: ISak['opprettetAv'];
    private readonly _lukketDato?: ISak['lukketDato'];
    private readonly _sakskategoriId?: ISak['sakskategoriId'];
    private readonly _sakskategori?: ISak['sakskategori'];
    private readonly _sakskategoriNavn?: ISak['sakskategoriNavn'];
    private readonly _kilde?: ISak['kilde'];
    private readonly _eksternUrl?: ISak['eksternUrl'];

    readonly _saksmeldingList: Array<Saksmelding> = [];
    readonly _extraSakskategoriList: Array<Sakskategori> = [];

    constructor(obj: ISak) {
        this._created = obj.created;
        this._emne = obj.emne;
        this._eksternSakId = obj.eksternSakId;
        this._id = obj.id;
        this._saksnummer = obj.saksnummer;
        this._isArchived = obj.isArchived;
        this._kundeNummer = obj.kundeNummer;
        this._opprettetAv = obj.opprettetAv;
        this._lukketDato = obj.lukketDato;
        this._status = obj.status;
        this._sakskategoriId = obj.sakskategoriId;
        this._sakskategori = obj.sakskategori;
        this._sakskategoriNavn = obj.sakskategori?.navn;
        this._kilde = obj.kilde;
        this._eksternUrl = obj.eksternUrl;

        this._saksmeldingList = obj.saksmeldingList || [];
        this._extraSakskategoriList = obj.ekstraSakskategoriList || [];
    }

    public toSak = () => {
        const {
            emne,
            status,
            sakskategoriId,
            kundeNummer,
            saksmeldingList,
            ekstraSakskategoriList,
        } = this;
        return {
            emne,
            status,
            sakskategoriId,
            kundeNummer,
            ekstraSakskategoriList,
            saksmeldingList: saksmeldingList.map((saksmelding) =>
                saksmelding.toSaksmelding(),
            ),
        };
    };
}
